import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ConfirmModal from '../UI/ConfirmModal';
import { SkeletonCard } from '../UI/SkeletonCard';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';

// Dish filters are remembered across navigation (edit a dish → come back) and
// page reloads, so the owner never has to re-pick the same category twice.
const FILTERS_KEY = 'dewanDishFilters';
const DEFAULT_FILTERS = {
  searchTerm: '',
  filterCategory: 'all',
  filterAvailability: 'all',
  sortBy: 'name'
};

function loadFilters() {
  try {
    const saved = localStorage.getItem(FILTERS_KEY);
    if (!saved) return DEFAULT_FILTERS;
    return { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export default function DishList({ showNotification }) {
  const { t } = useLang();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  // One object so it can be persisted (and restored) in a single step.
  const [filters, setFilters] = useState(loadFilters);
  const { searchTerm, filterCategory, filterAvailability, sortBy } = filters;
  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters(DEFAULT_FILTERS);
  const [confirmModal, setConfirmModal] = useState({ open: false, dishId: null, dishName: '' });

  // Category management modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // { name, nameZh }
  const [editName, setEditName] = useState('');
  const [editNameZh, setEditNameZh] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [deletingCat, setDeletingCat] = useState(null); // category name pending delete confirm

  const token = localStorage.getItem('ownerToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDishes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/dishes/all-dishes`, { headers });
      if (res.data.success) setDishes(res.data.data);
      else showNotification('Failed to load dishes', 'error');
    } catch {
      showNotification('Failed to load dishes. Check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchDishes(); }, [fetchDishes]);

  // Remember the current filters for the next visit.
  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* storage unavailable */ }
  }, [filters]);

  // A remembered category can disappear (renamed or deleted). Fall back to "all"
  // instead of silently showing an empty grid.
  useEffect(() => {
    if (loading || filterCategory === 'all' || dishes.length === 0) return;
    if (!dishes.some(d => d.category === filterCategory)) {
      setFilter('filterCategory', 'all');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishes, loading, filterCategory]);

  const handleDelete = async () => {
    const { dishId } = confirmModal;
    setConfirmModal({ open: false, dishId: null, dishName: '' });
    try {
      const res = await axios.delete(`${API_BASE_URL}/dishes/delete-dish/${dishId}`, { headers });
      if (res.data.success) {
        setDishes(prev => prev.filter(d => d._id !== dishId));
        showNotification('Dish deleted successfully!', 'success');
      } else {
        showNotification('Failed to delete dish', 'error');
      }
    } catch {
      showNotification('Failed to delete dish. Please try again.', 'error');
    }
  };

  const handleToggleAvailability = async (dish) => {
    try {
      const res = await axios.patch(`${API_BASE_URL}/dishes/toggle-availability/${dish._id}`, {}, { headers });
      if (res.data.success) {
        setDishes(prev => prev.map(d => d._id === dish._id ? { ...d, available: res.data.data.available } : d));
        showNotification(`"${dish.name}" is now ${res.data.data.available ? 'available' : 'unavailable'}`, 'success');
      }
    } catch {
      showNotification('Failed to toggle availability', 'error');
    }
  };

  const handleRenameCategory = async () => {
    if (!editName.trim()) return;
    setCatSaving(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/dishes/categories/rename`, {
        oldName: editingCat.name,
        newName: editName.trim(),
        newNameZh: editNameZh.trim()
      }, { headers });
      if (res.data.success) {
        showNotification(`Category renamed — ${res.data.updatedCount} dish(es) updated`, 'success');
        setDishes(prev => prev.map(d =>
          d.category === editingCat.name
            ? { ...d, category: editName.trim(), categoryZh: editNameZh.trim() || d.categoryZh }
            : d
        ));
        setEditingCat(null);
      } else {
        showNotification('Failed to rename category', 'error');
      }
    } catch {
      showNotification('Failed to rename category', 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (catName) => {
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/dishes/categories/${encodeURIComponent(catName)}`,
        { headers }
      );
      if (res.data.success) {
        showNotification(`"${catName}" deleted — ${res.data.deletedCount} dish(es) removed`, 'success');
        setDishes(prev => prev.filter(d => d.category !== catName));
        setDeletingCat(null);
      } else {
        showNotification('Failed to delete category', 'error');
      }
    } catch {
      showNotification('Failed to delete category', 'error');
    }
  };

  const categories = ['all', ...Array.from(new Set(dishes.map(d => d.category)))];

  const filtersActive =
    searchTerm !== DEFAULT_FILTERS.searchTerm ||
    filterCategory !== DEFAULT_FILTERS.filterCategory ||
    filterAvailability !== DEFAULT_FILTERS.filterAvailability ||
    sortBy !== DEFAULT_FILTERS.sortBy;

  const filtered = dishes
    .filter(d => {
      const q = searchTerm.toLowerCase();
      if (!q) return true;
      return d.name.toLowerCase().includes(q)
        || (d.nameZh || '').toLowerCase().includes(q)
        || (d.dishNumber || '').toLowerCase().includes(q);
    })
    .filter(d => filterCategory === 'all' || d.category === filterCategory)
    .filter(d => filterAvailability === 'all' || (filterAvailability === 'available' ? d.available : !d.available))
    .sort((a, b) => {
      if (sortBy === 'price') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'popular') return (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0);
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="dish-list-page">
      <ConfirmModal
        isOpen={confirmModal.open}
        title="Delete Dish"
        message={`Are you sure you want to delete "${confirmModal.dishName}"? This cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmModal({ open: false, dishId: null, dishName: '' })}
      />

      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-grid-1x2-fill me-2"></i>{t('dishes.title')}</h2>
          <p className="page-subtitle">{dishes.length} {t('dishes.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button onClick={fetchDishes} className="btn-secondary-action">
            <i className="bi bi-arrow-clockwise me-1"></i>{t('common.refresh')}
          </button>
          <button onClick={() => { setShowCatModal(true); setEditingCat(null); setDeletingCat(null); }} className="btn-secondary-action">
            <i className="bi bi-tags me-1"></i>{t('dishes.manageCategories')}
          </button>
          <Link to="/create-dish" className="btn-primary-action">
            <i className="bi bi-plus-circle me-2"></i>{t('dishes.addDish')}
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="dish-filters">
        <input
          type="text"
          className="filter-input"
          placeholder={t('dishes.search')}
          value={searchTerm}
          onChange={e => setFilter('searchTerm', e.target.value)}
        />
        <select className="filter-select" value={filterCategory} onChange={e => setFilter('filterCategory', e.target.value)}>
          {categories.map(c => <option key={c} value={c}>{c === 'all' ? t('common.all') : c}</option>)}
        </select>
        <select className="filter-select" value={filterAvailability} onChange={e => setFilter('filterAvailability', e.target.value)}>
          <option value="all">{t('common.all')}</option>
          <option value="available">{t('dishes.available')}</option>
          <option value="unavailable">{t('dishes.unavailable')}</option>
        </select>
        <select className="filter-select" value={sortBy} onChange={e => setFilter('sortBy', e.target.value)}>
          <option value="name">Sort: Name</option>
          <option value="price">Price: Low → High</option>
          <option value="price_desc">Price: High → Low</option>
          <option value="popular">Popular First</option>
          <option value="rating">Top Rated</option>
        </select>
        {filtersActive && (
          <button className="btn-clear-filters" onClick={clearFilters} title={t('dishes.clearFilters')}>
            <i className="bi bi-x-circle me-1"></i>{t('dishes.clearFilters')}
          </button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="dish-grid">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-emoji-frown"></i>
          <p>No dishes found. Try adjusting your filters.</p>
          {dishes.length === 0 && (
            <Link to="/create-dish" className="btn-primary-action mt-3">
              <i className="bi bi-plus-circle me-2"></i>Add Your First Dish
            </Link>
          )}
        </div>
      ) : (
        <motion.div className="dish-grid" layout>
          <AnimatePresence>
            {filtered.map((dish, i) => (
              <motion.div
                key={dish._id}
                className={`dish-card ${!dish.available ? 'dish-card-unavailable' : ''}`}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: i * 0.04 }}
                whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(160,90,44,0.2)' }}
              >
                {/* Image */}
                <div className="dish-card-img-wrap">
                  {dish.image ? (
                    <img
                      src={dish.image}
                      alt={dish.name}
                      className="dish-card-img"
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  <div
                    className="dish-card-no-image"
                    style={{ display: dish.image ? 'none' : 'flex' }}
                  >
                    <i className="bi bi-cup-hot-fill"></i>
                    <span>No image</span>
                  </div>
                  <div className="dish-card-badges">
                    {dish.isPopular && (
                      <span className="badge-popular"><i className="bi bi-star-fill me-1"></i>{t('dishes.popular')}</span>
                    )}
                    {!dish.available && (
                      <span className="badge-unavailable">{t('dishes.unavailable')}</span>
                    )}
                    {Array.isArray(dish.variants) && dish.variants.length > 0 && (
                      <span
                        className="badge-variants"
                        style={{
                          background: '#FFF8E7',
                          color: '#8B4A24',
                          border: '1px solid #c9a227',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                        title={`${dish.variants.length} sizes`}
                      >
                        <i className="bi bi-rulers me-1"></i>{dish.variants.length} sizes
                      </span>
                    )}
                    {!dish.nameZh && (
                      <span
                        className="badge-missing-zh"
                        style={{
                          background: '#fff3cd',
                          color: '#856404',
                          border: '1px solid #ffeaa7',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                        title="No Chinese name set — edit to add one"
                      >
                        ⚠ 中文 missing
                      </span>
                    )}
                  </div>
                  {dish.discountPrice && (!dish.variants || dish.variants.length === 0) && (
                    <div className="dish-card-discount">
                      <i className="bi bi-tag-fill me-1"></i>{t('dishes.sale')}
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="dish-card-body">
                  {dish.dishNumber && (
                    <span className="dish-card-number" title={t('dishes.dishNumber')}>
                      <i className="bi bi-hash"></i>{dish.dishNumber}
                    </span>
                  )}
                  <h5 className="dish-card-name">{dish.name}</h5>
                  {dish.nameZh && (
                    <p className="dish-card-name-zh" style={{ fontSize: '13px', color: '#a88860', margin: '-2px 0 6px', fontWeight: 500 }}>
                      {dish.nameZh}
                    </p>
                  )}
                  <div className="dish-card-meta">
                    <span className="dish-card-category">
                      {dish.category}{dish.categoryZh ? ` / ${dish.categoryZh}` : ''}
                    </span>
                  </div>

                  <div className="dish-card-price-row">
                    {Array.isArray(dish.variants) && dish.variants.length > 0 ? (
                      <div className="dish-card-variants-pricelist">
                        {dish.variants.map((v, vi) => (
                          <span className="dish-card-variant-pill" key={vi}>
                            <strong>¥{Number(v.price).toFixed(2)}</strong>
                            <small>/{v.label}</small>
                          </span>
                        ))}
                      </div>
                    ) : dish.discountPrice ? (
                      <>
                        <span className="dish-card-price-sale">¥{Number(dish.discountPrice).toFixed(2)}</span>
                        <span className="dish-card-price-original">¥{Number(dish.price).toFixed(2)}</span>
                      </>
                    ) : (
                      <span className="dish-card-price">¥{Number(dish.price).toFixed(2)}</span>
                    )}
                    {(dish.rating > 0) && (
                      <span className="dish-card-rating">
                        <i className="bi bi-star-fill me-1" style={{ color: '#c9a227' }}></i>
                        {Number(dish.rating).toFixed(1)} ({dish.reviewCount})
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="dish-card-actions">
                    <button
                      className={`btn-toggle-avail ${dish.available ? 'available' : 'unavailable'}`}
                      onClick={() => handleToggleAvailability(dish)}
                      title={dish.available ? t('dishes.unavailable') : t('dishes.available')}
                    >
                      <i className={`bi ${dish.available ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
                      {dish.available ? t('dishes.available') : t('dishes.unavailable')}
                    </button>
                    <Link to={`/edit-dish/${dish._id}`} className="btn-edit-icon" title={t('dishes.edit')}>
                      <i className="bi bi-pencil-square"></i>
                    </Link>
                    <button
                      className="btn-delete-icon"
                      onClick={() => setConfirmModal({ open: true, dishId: dish._id, dishName: dish.name })}
                      title="Delete dish"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <p className="dish-count-info">
        Showing {filtered.length} of {dishes.length} dishes
      </p>

      {/* Manage Categories Modal */}
      <AnimatePresence>
        {showCatModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { setShowCatModal(false); setEditingCat(null); setDeletingCat(null); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#3d1f0a' }}>
                  <i className="bi bi-tags me-2" style={{ color: '#a05a2c' }}></i>Manage Categories
                </h3>
                <button
                  onClick={() => { setShowCatModal(false); setEditingCat(null); setDeletingCat(null); }}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1 }}
                >✕</button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1 }}>
                {Array.from(new Set(dishes.map(d => d.category))).sort().map(catName => {
                  const catZh = dishes.find(d => d.category === catName)?.categoryZh || '';
                  const count = dishes.filter(d => d.category === catName).length;
                  const isEditing = editingCat?.name === catName;
                  const isDeleting = deletingCat === catName;

                  return (
                    <div key={catName} style={{ borderBottom: '1px solid #f0e8df', paddingBottom: 12, marginBottom: 12 }}>
                      {isEditing ? (
                        <div>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <input
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              placeholder="English name"
                              className="form-input"
                              style={{ flex: 1 }}
                              autoFocus
                            />
                            <input
                              value={editNameZh}
                              onChange={e => setEditNameZh(e.target.value)}
                              placeholder="中文 (optional)"
                              className="form-input"
                              style={{ flex: 1 }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={handleRenameCategory}
                              disabled={catSaving || !editName.trim()}
                              className="btn-primary-action"
                              style={{ fontSize: 13, padding: '6px 14px' }}
                            >
                              {catSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingCat(null)}
                              className="btn-secondary-action"
                              style={{ fontSize: 13, padding: '6px 14px' }}
                            >Cancel</button>
                          </div>
                        </div>
                      ) : isDeleting ? (
                        <div style={{ background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 10, padding: '10px 14px' }}>
                          <p style={{ margin: '0 0 10px', fontSize: 13, color: '#c0392b' }}>
                            Delete <strong>"{catName}"</strong>? This will permanently delete all <strong>{count} dish(es)</strong> in this category.
                          </p>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => handleDeleteCategory(catName)}
                              style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >Yes, Delete All</button>
                            <button
                              onClick={() => setDeletingCat(null)}
                              className="btn-secondary-action"
                              style={{ fontSize: 13, padding: '6px 14px' }}
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 600, color: '#3d1f0a', fontSize: 15 }}>{catName}</span>
                            {catZh && <span style={{ color: '#a88860', fontSize: 13, marginLeft: 8 }}>/ {catZh}</span>}
                            <span style={{ marginLeft: 10, background: '#f5ede4', color: '#8B4513', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>{count} dish{count !== 1 ? 'es' : ''}</span>
                          </div>
                          <button
                            onClick={() => { setEditingCat({ name: catName, nameZh: catZh }); setEditName(catName); setEditNameZh(catZh); setDeletingCat(null); }}
                            title="Edit category name"
                            style={{ background: '#f5ede4', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#a05a2c', fontSize: 14 }}
                          ><i className="bi bi-pencil-square"></i></button>
                          <button
                            onClick={() => { setDeletingCat(catName); setEditingCat(null); }}
                            title="Delete category"
                            style={{ background: '#fff0f0', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#e74c3c', fontSize: 14 }}
                          ><i className="bi bi-trash"></i></button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {dishes.length === 0 && (
                  <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>No categories yet. Add a dish first.</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
